/**
 * Metals & Steel Industry Configuration
 *
 * Supports: Blast Furnaces, Rolling Mills, Casters, Ladles, Cranes, Annealing Furnaces
 */

import { IndustryConfig } from '../industryTypes'

export const metalsConfig: IndustryConfig = {
  id: 'metals',
  name: 'Metals & Steel',
  description: 'Steel production and metals processing equipment monitoring',
  icon: 'Anvil',

  terminology: {
    asset: 'Mill Equipment',
    assets: 'Mill Equipment',
    assetPlural: 'Mill Equipment',
    assetType: 'Equipment Type',
    failure: 'Mill Failure',
    health: 'Condition',
    healthScore: 'Condition Score',
    healthy: 'Operating',
    degraded: 'Degraded',
    critical: 'Down',
    alert: 'Mill Alert',
    process: 'Production',
    maintenance: 'Maintenance',
    inspection: 'Inspection',
    downtime: 'Outage',
    workOrder: 'Work Order',
    prediction: 'Forecast',
    anomaly: 'Anomaly',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Mill',
    facilityPlural: 'Mills',
    location: 'Bay',
    operator: 'Mill Operator',
    supervisor: 'Shift Foreman',
    calibration: 'Calibration',
    compliance: 'Compliance',
    criticality: 'Criticality',
  },

  assetTypes: [
    {
      id: 'blast_furnace',
      name: 'Blast Furnace',
      icon: 'Flame',
      description: 'Iron-making blast furnace for converting iron ore to pig iron',
      category: 'Primary',
      defaultMetrics: ['hot_blast_temperature', 'top_pressure', 'burden_descent_rate', 'slag_basicity'],
      maintenanceTypes: ['tuyere_inspection', 'refractory_repair', 'cooling_system_service', 'charging_system_maintenance'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Core iron-making equipment', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major production impact', downtimeCostMultiplier: 25 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Moderate impact', downtimeCostMultiplier: 10 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Minimal impact', downtimeCostMultiplier: 3 },
      ],
    },
    {
      id: 'rolling_mill',
      name: 'Rolling Mill',
      icon: 'Cog',
      description: 'Hot and cold rolling mills for shaping steel products',
      category: 'Rolling',
      defaultMetrics: ['roll_force', 'strip_tension', 'roll_gap', 'rolling_speed', 'temperature'],
      maintenanceTypes: ['roll_change', 'bearing_service', 'hydraulic_service', 'drive_alignment'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Main production line', downtimeCostMultiplier: 40 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary rolling stand', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary stand', downtimeCostMultiplier: 8 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Auxiliary equipment', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'caster',
      name: 'Continuous Caster',
      icon: 'ArrowDownToLine',
      description: 'Continuous casting machine for solidifying molten steel into slabs or billets',
      category: 'Casting',
      defaultMetrics: ['casting_speed', 'mold_level', 'secondary_cooling', 'breakout_prediction'],
      maintenanceTypes: ['mold_inspection', 'segment_alignment', 'spray_nozzle_replacement', 'oscillation_service'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary caster', downtimeCostMultiplier: 45 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major casting impact', downtimeCostMultiplier: 22 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary systems', downtimeCostMultiplier: 9 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Support equipment', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'ladle',
      name: 'Ladle',
      icon: 'Container',
      description: 'Steel ladles for transporting and treating molten steel',
      category: 'Steelmaking',
      defaultMetrics: ['shell_temperature', 'refractory_thickness', 'slide_gate_condition', 'heat_loss'],
      maintenanceTypes: ['refractory_reline', 'slide_gate_service', 'trunnion_inspection', 'preheat_verification'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Active production ladle', downtimeCostMultiplier: 30 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary backup ladle', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary backup', downtimeCostMultiplier: 6 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Reserve ladle', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'crane',
      name: 'Overhead Crane',
      icon: 'Crane',
      description: 'Heavy-duty overhead cranes for material handling in steel mills',
      category: 'Material Handling',
      defaultMetrics: ['hoist_load', 'bridge_travel', 'trolley_position', 'brake_condition'],
      maintenanceTypes: ['wire_rope_inspection', 'brake_service', 'motor_service', 'rail_alignment'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Hot metal crane', downtimeCostMultiplier: 35 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Charging crane', downtimeCostMultiplier: 18 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Mill service crane', downtimeCostMultiplier: 7 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Maintenance crane', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'annealing_furnace',
      name: 'Annealing Furnace',
      icon: 'Thermometer',
      description: 'Heat treatment furnace for annealing steel products',
      category: 'Heat Treatment',
      defaultMetrics: ['zone_temperatures', 'atmosphere_composition', 'strip_tension', 'line_speed'],
      maintenanceTypes: ['radiant_tube_inspection', 'roll_inspection', 'atmosphere_system_service', 'seal_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Continuous annealing line', downtimeCostMultiplier: 38 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Batch annealing furnace', downtimeCostMultiplier: 19 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary furnace', downtimeCostMultiplier: 8 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Auxiliary heating', downtimeCostMultiplier: 2 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'production_throughput',
      name: 'Production Throughput',
      icon: 'TrendingUp',
      description: 'Steel production output and throughput metrics',
      businessValue: 'Maximize steel production and minimize delays',
      category: 'universal',
      metrics: [
        { id: 'tons_per_hour', name: 'Tons Per Hour', unit: 't/hr', description: 'Steel production rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 200, warning: 150, critical: 100 } },
        { id: 'yield_rate', name: 'Yield Rate', unit: '%', description: 'Good product vs raw material input', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 90, critical: 85 } },
        { id: 'casting_speed', name: 'Casting Speed', unit: 'm/min', description: 'Continuous caster speed', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 1.5, warning: 1.2, critical: 0.8 } },
        { id: 'rolling_speed', name: 'Rolling Speed', unit: 'm/s', description: 'Hot strip mill rolling speed', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 20, warning: 15, critical: 10 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['line_chart', 'bar_chart', 'gauge', 'trend'],
    },
    {
      id: 'quality_control',
      name: 'Quality Control',
      icon: 'CheckCircle',
      description: 'Steel quality and product conformance metrics',
      businessValue: 'Ensure product quality and reduce downgrades',
      category: 'industry_specific',
      metrics: [
        { id: 'surface_defects', name: 'Surface Defects', unit: 'defects/km', description: 'Surface defect rate per kilometer', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
        { id: 'thickness_deviation', name: 'Thickness Deviation', unit: 'mm', description: 'Deviation from target thickness', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.02, warning: 0.05, critical: 0.10 } },
        { id: 'chemistry_compliance', name: 'Chemistry Compliance', unit: '%', description: 'Heats meeting chemistry specs', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99, warning: 95, critical: 90 } },
        { id: 'mechanical_properties', name: 'Mechanical Properties', unit: '%', description: 'Products meeting mechanical specs', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 94, critical: 88 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 90, max: 95 }, critical: { max: 90 } },
      visualizations: ['bar_chart', 'trend', 'distribution', 'comparison'],
    },
    {
      id: 'energy_efficiency',
      name: 'Energy Efficiency',
      icon: 'Zap',
      description: 'Energy consumption and efficiency metrics',
      businessValue: 'Reduce energy costs and environmental impact',
      category: 'universal',
      metrics: [
        { id: 'energy_per_ton', name: 'Energy Per Ton', unit: 'GJ/t', description: 'Total energy consumption per ton of steel', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 18, warning: 22, critical: 28 } },
        { id: 'blast_furnace_coke_rate', name: 'Coke Rate', unit: 'kg/t', description: 'Coke consumption per ton of hot metal', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 350, warning: 400, critical: 450 } },
        { id: 'natural_gas_usage', name: 'Natural Gas Usage', unit: 'm3/t', description: 'Natural gas per ton produced', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 50, warning: 65, critical: 80 } },
        { id: 'electricity_usage', name: 'Electricity Usage', unit: 'kWh/t', description: 'Electrical power per ton', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 450, warning: 550, critical: 700 } },
      ],
      thresholds: { healthy: { max: 20 }, warning: { min: 20, max: 25 }, critical: { min: 25 } },
      visualizations: ['line_chart', 'bar_chart', 'comparison', 'trend'],
    },
    {
      id: 'safety',
      name: 'Safety Metrics',
      icon: 'Shield',
      description: 'Safety performance and incident tracking',
      businessValue: 'Protect workers and maintain safe operations',
      category: 'universal',
      metrics: [
        { id: 'trir', name: 'Total Recordable Incident Rate', unit: 'incidents/200k hrs', description: 'OSHA recordable incident rate', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 1.0, warning: 2.5, critical: 5.0 } },
        { id: 'near_misses', name: 'Near Miss Reports', unit: 'count', description: 'Reported near miss incidents', dataType: 'continuous', aggregation: 'sum', goodDirection: 'higher', defaultThresholds: { healthy: 20, warning: 10, critical: 5 } },
        { id: 'safety_observations', name: 'Safety Observations', unit: 'count', description: 'Safety observations completed', dataType: 'continuous', aggregation: 'sum', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 50, critical: 25 } },
        { id: 'hot_metal_incidents', name: 'Hot Metal Incidents', unit: 'count', description: 'Hot metal spill or splash incidents', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 1, critical: 3 } },
      ],
      thresholds: { healthy: { max: 1 }, warning: { min: 1, max: 3 }, critical: { min: 3 } },
      visualizations: ['bar_chart', 'trend', 'gauge', 'comparison'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'preventive',
      name: 'Preventive Maintenance',
      description: 'Scheduled time-based maintenance for mill equipment',
      category: 'preventive',
      defaultInterval: { value: 720, unit: 'hours' },
      laborRequirements: [
        { role: 'Mill Technician', count: 2, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['mechanical', 'hydraulics', 'high_temperature'],
      partsCommonly: ['lubricants', 'filters', 'gaskets', 'wear_plates'],
    },
    {
      id: 'corrective',
      name: 'Corrective Maintenance',
      description: 'Repair after equipment failure or malfunction',
      category: 'corrective',
      laborRequirements: [
        { role: 'Mill Technician', count: 3, hourlyRate: 55 },
        { role: 'Metallurgist', count: 1, hourlyRate: 95 },
      ],
      estimatedDuration: { min: 4, typical: 12, max: 48, unit: 'hours' },
      skillsRequired: ['troubleshooting', 'mechanical', 'electrical', 'hydraulics', 'refractory'],
      partsCommonly: ['motors', 'bearings', 'rolls', 'refractories', 'hydraulic_components'],
    },
    {
      id: 'predictive',
      name: 'Predictive Maintenance',
      description: 'Condition-based maintenance triggered by monitoring data',
      category: 'predictive',
      laborRequirements: [
        { role: 'Mill Technician', count: 2, hourlyRate: 55 },
        { role: 'Process Analyst', count: 1, hourlyRate: 75 },
      ],
      estimatedDuration: { min: 2, typical: 6, max: 16, unit: 'hours' },
      skillsRequired: ['diagnostics', 'vibration_analysis', 'thermography', 'data_analysis'],
      partsCommonly: ['bearings', 'seals', 'rolls', 'sensors'],
    },
    {
      id: 'major_overhaul',
      name: 'Major Overhaul',
      description: 'Comprehensive equipment rebuild or reline',
      category: 'condition_based',
      defaultInterval: { value: 60, unit: 'months' },
      laborRequirements: [
        { role: 'Mill Technician', count: 8, hourlyRate: 55 },
        { role: 'Metallurgist', count: 2, hourlyRate: 95 },
        { role: 'Contractor', count: 10, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 7, typical: 21, max: 45, unit: 'days' },
      skillsRequired: ['mechanical', 'electrical', 'refractory', 'hydraulics', 'rigging', 'welding'],
      partsCommonly: ['rolls', 'refractories', 'bearings', 'hydraulic_cylinders', 'motors', 'gearboxes'],
    },
  ],

  riskFactors: [
    { id: 'safety', name: 'Safety Risk', description: 'Risk of worker injury from hot metal, moving equipment, or hazardous conditions', category: 'safety', weight: 0.35, calculationMethod: 'threshold', inputs: ['hot_metal_exposure', 'guarding_status', 'lockout_compliance'] },
    { id: 'production_loss', name: 'Production Loss Risk', description: 'Risk of production interruption or reduced output', category: 'operational', weight: 0.30, calculationMethod: 'exponential', inputs: ['health_score', 'criticality', 'redundancy'] },
    { id: 'energy_cost', name: 'Energy Cost Risk', description: 'Risk of excessive energy consumption', category: 'financial', weight: 0.20, calculationMethod: 'linear', inputs: ['efficiency_trend', 'fuel_consumption', 'electricity_usage'] },
    { id: 'quality', name: 'Quality Risk', description: 'Risk of product quality issues or downgrades', category: 'operational', weight: 0.15, calculationMethod: 'linear', inputs: ['defect_trend', 'process_capability', 'calibration_status'] },
  ],

  complianceStandards: [
    {
      id: 'iso_9001',
      name: 'ISO 9001 Quality Management',
      shortName: 'ISO 9001',
      description: 'Quality management system standard for consistent product quality',
      authority: 'ISO',
      region: ['Global'],
      requirements: [
        { id: 'calibration_current', name: 'Calibration Status', description: 'All measurement equipment calibrated', kpiId: 'calibration_status', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'documentation_complete', name: 'Documentation', description: 'Complete quality documentation', kpiId: 'documentation_complete', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'traceability', name: 'Product Traceability', description: 'Full heat and coil traceability', kpiId: 'traceability_compliance', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'iso_14001',
      name: 'ISO 14001 Environmental Management',
      shortName: 'ISO 14001',
      description: 'Environmental management system for minimizing environmental impact',
      authority: 'ISO',
      region: ['Global'],
      requirements: [
        { id: 'emissions_monitoring', name: 'Emissions Monitoring', description: 'Continuous emissions monitoring in compliance', kpiId: 'emissions_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'waste_management', name: 'Waste Management', description: 'Proper slag and waste disposal', kpiId: 'waste_compliance', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'water_discharge', name: 'Water Discharge', description: 'Water discharge within permitted limits', kpiId: 'water_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'osha_steel',
      name: 'OSHA Steel Industry Standards',
      shortName: 'OSHA Steel',
      description: 'Occupational safety requirements specific to steel industry',
      authority: 'US Department of Labor',
      region: ['US'],
      requirements: [
        { id: 'hot_metal_ppe', name: 'Hot Metal PPE', description: 'Proper PPE for hot metal operations', kpiId: 'ppe_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'crane_inspection', name: 'Crane Inspections', description: 'Regular overhead crane inspections', kpiId: 'crane_inspection_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'confined_space', name: 'Confined Space Entry', description: 'Confined space entry procedures compliance', kpiId: 'confined_space_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'lockout_tagout', name: 'Lockout/Tagout', description: 'LOTO procedures for energy isolation', kpiId: 'loto_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
  ],

  visualization: {
    primaryColor: '#f97316',
    secondaryColor: '#6b7280',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Condition Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'prediction_alert',
      name: 'Forecast Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: 'AI {prediction} for {assetType} "{assetName}": {predictionDetails}. Confidence: {confidence}%. Based on analysis of {dataPoints} data points. Recommended action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
    {
      id: 'maintenance_summary',
      name: 'Maintenance Summary',
      context: 'maintenance',
      audience: ['manager', 'technician'],
      template: '{maintenanceType} completed on {assetType} "{assetName}" in {location}. Duration: {duration}. Parts used: {partsUsed}. Next scheduled {maintenance}: {nextDate}.',
      variables: [
        { name: 'maintenanceType', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'location', type: 'terminology' },
        { name: 'duration', type: 'time' },
        { name: 'partsUsed', type: 'metric' },
        { name: 'maintenance', type: 'terminology' },
        { name: 'nextDate', type: 'time' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '7d',
    refreshInterval: 30,
    defaultDashboard: 'command-center',
  },

  // =============================================================================
  // PROFESSIONAL ROLES (Metals & Steel-Specific)
  // =============================================================================
  professionalRoles: [
    // SME Roles
    {
      id: 'metallurgist',
      category: 'sme',
      name: 'Metallurgist',
      shortName: 'MET',
      description: 'Expert in steel chemistry, microstructure, and process metallurgy',
      responsibilities: [
        'Steel chemistry optimization',
        'Process parameter development',
        'Quality troubleshooting',
        'New grade development',
        'Failure analysis',
      ],
      focusAreas: ['chemistry_compliance', 'mechanical_properties', 'microstructure', 'heat_treatment', 'defect_analysis'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'risk', 'action_items'],
      },
      decisionAuthority: ['chemistry_targets', 'process_parameters', 'quality_disposition'],
      reportingTo: 'Technical Director',
      icon: 'FlaskConical',
    },
    {
      id: 'process_engineer',
      category: 'sme',
      name: 'Process Engineer',
      shortName: 'PE',
      description: 'Optimizes steel production processes for efficiency and quality',
      responsibilities: [
        'Process optimization',
        'Equipment capability analysis',
        'Throughput improvement',
        'Energy efficiency projects',
        'Process automation',
      ],
      focusAreas: ['throughput', 'yield', 'energy_efficiency', 'process_capability', 'automation'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'action_items', 'business_impact'],
      },
      decisionAuthority: ['process_parameters', 'equipment_settings', 'production_schedule'],
      reportingTo: 'Operations Manager',
      icon: 'Settings',
    },
    {
      id: 'reliability_engineer',
      category: 'sme',
      name: 'Reliability Engineer',
      shortName: 'RE',
      description: 'Analyzes equipment reliability and develops maintenance strategies',
      responsibilities: [
        'Equipment reliability analysis',
        'Root cause failure analysis',
        'Maintenance strategy development',
        'Spare parts optimization',
        'Predictive maintenance programs',
      ],
      focusAreas: ['MTBF', 'MTTR', 'failure_patterns', 'maintenance_costs', 'availability'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'risk', 'action_items'],
      },
      decisionAuthority: ['maintenance_strategy', 'spare_parts_stocking', 'equipment_modifications'],
      reportingTo: 'Maintenance Manager',
      icon: 'Target',
    },
    // Technician Roles
    {
      id: 'mill_technician',
      category: 'technician',
      name: 'Mill Technician',
      shortName: 'MT',
      description: 'Performs hands-on maintenance and repair of mill equipment',
      responsibilities: [
        'Equipment troubleshooting and repair',
        'Roll changes and alignment',
        'Hydraulic system maintenance',
        'Bearing replacements',
        'Preventive maintenance execution',
      ],
      focusAreas: ['fault_codes', 'repair_procedures', 'parts_needed', 'safety_requirements', 'tool_requirements'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['repair_methods', 'parts_selection', 'immediate_safety_stops'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Wrench',
    },
    {
      id: 'refractory_technician',
      category: 'technician',
      name: 'Refractory Technician',
      shortName: 'RT',
      description: 'Specializes in refractory installation, repair, and monitoring',
      responsibilities: [
        'Refractory installation and repair',
        'Ladle and vessel relining',
        'Refractory wear monitoring',
        'Tundish preparation',
        'Preheating operations',
      ],
      focusAreas: ['refractory_thickness', 'wear_patterns', 'lining_life', 'material_selection'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['refractory_repairs', 'material_selection', 'vessel_readiness'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Layers',
    },
    {
      id: 'crane_operator',
      category: 'technician',
      name: 'Crane Operator',
      shortName: 'CO',
      description: 'Operates overhead cranes for material handling in steel mill',
      responsibilities: [
        'Hot metal and ladle handling',
        'Coil and slab transport',
        'Scrap charging',
        'Pre-operation inspections',
        'Load weight verification',
      ],
      focusAreas: ['load_limits', 'rigging_requirements', 'safety_procedures', 'equipment_condition'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['action_items', 'risk'],
      },
      decisionAuthority: ['lift_execution', 'safety_stops', 'equipment_deficiency_reports'],
      reportingTo: 'Shift Foreman',
      icon: 'Crane',
    },
    // Executive Roles
    {
      id: 'operations_director',
      category: 'executive',
      name: 'Operations Director',
      shortName: 'OD',
      description: 'Oversees all mill operations, production targets, and strategic initiatives',
      responsibilities: [
        'Overall mill performance',
        'Production planning and targets',
        'Capital project oversight',
        'Safety leadership',
        'Cost management',
      ],
      focusAreas: ['production_throughput', 'safety_metrics', 'cost_per_ton', 'quality_performance', 'on_time_delivery'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['capital_expenditure', 'production_targets', 'strategic_initiatives'],
      reportingTo: 'VP Operations',
      icon: 'Building',
    },
    {
      id: 'maintenance_manager',
      category: 'executive',
      name: 'Maintenance Manager',
      shortName: 'MM',
      description: 'Manages maintenance department, budgets, and equipment reliability programs',
      responsibilities: [
        'Maintenance budget management',
        'Equipment reliability improvement',
        'Outage planning and execution',
        'Contractor management',
        'Team development',
      ],
      focusAreas: ['maintenance_costs', 'equipment_availability', 'mtbf', 'backlog_hours', 'team_utilization'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['business_impact', 'action_items', 'risk'],
      },
      decisionAuthority: ['maintenance_budget', 'equipment_replacement', 'staff_scheduling'],
      reportingTo: 'Operations Director',
      icon: 'Users',
    },
    // Analyst Roles
    {
      id: 'process_analyst',
      category: 'analyst',
      name: 'Process Analyst',
      shortName: 'PA',
      description: 'Analyzes production and equipment data to identify trends and opportunities',
      responsibilities: [
        'Production data analysis',
        'Performance reporting',
        'Trend identification',
        'Dashboard development',
        'KPI tracking and reporting',
      ],
      focusAreas: ['all_metrics', 'trends', 'correlations', 'benchmarks', 'anomalies'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact'],
      },
      decisionAuthority: ['report_formats', 'data_collection_methods', 'analysis_priorities'],
      reportingTo: 'Operations Manager',
      icon: 'BarChart',
    },
    {
      id: 'quality_analyst',
      category: 'analyst',
      name: 'Quality Analyst',
      shortName: 'QA',
      description: 'Monitors and analyzes product quality data',
      responsibilities: [
        'Quality data collection and analysis',
        'Defect tracking and trending',
        'Customer complaint investigation',
        'Quality reporting',
        'SPC monitoring',
      ],
      focusAreas: ['surface_defects', 'dimensional_accuracy', 'chemistry_compliance', 'mechanical_properties'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'risk', 'action_items'],
      },
      decisionAuthority: ['quality_holds', 'inspection_frequency', 'test_methods'],
      reportingTo: 'Quality Manager',
      icon: 'ClipboardCheck',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Metals & Steel Equipment)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'rolls',
        name: 'Rolls',
        description: 'Work rolls, backup rolls, and roll components for rolling mills',
        parts: [
          {
            id: 'work_roll_hsm',
            partNumber: 'WR-HSM-800',
            name: 'Hot Strip Mill Work Roll',
            description: '800mm diameter high-chrome work roll for hot strip mill',
            category: 'rolls',
            manufacturer: 'Union Electric',
            unitCost: 85000.00,
            currency: 'USD',
            leadTime: { value: 12, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill'],
            failureModes: ['surface_wear', 'spalling', 'thermal_cracking'],
            averageLifespan: { value: 50000, unit: 'cycles' },
            specifications: { diameter: '800mm', length: '2100mm', material: 'High Chrome Steel' },
          },
          {
            id: 'backup_roll',
            partNumber: 'BR-HSM-1500',
            name: 'Backup Roll',
            description: '1500mm diameter forged steel backup roll',
            category: 'rolls',
            manufacturer: 'Akers',
            unitCost: 250000.00,
            currency: 'USD',
            leadTime: { value: 20, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill'],
            failureModes: ['surface_fatigue', 'bearing_seat_wear', 'journal_damage'],
            averageLifespan: { value: 500000, unit: 'cycles' },
            specifications: { diameter: '1500mm', length: '2200mm', material: 'Forged Steel' },
          },
          {
            id: 'caster_roll',
            partNumber: 'CR-CC-300',
            name: 'Caster Segment Roll',
            description: '300mm diameter continuous caster segment roll',
            category: 'rolls',
            manufacturer: 'SMS Group',
            unitCost: 12000.00,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['caster'],
            failureModes: ['surface_cracking', 'bearing_failure', 'shell_deformation'],
            averageLifespan: { value: 300000, unit: 'cycles' },
            specifications: { diameter: '300mm', length: '1800mm', cooling: 'Internal water cooling' },
          },
        ],
      },
      {
        id: 'bearings',
        name: 'Bearings',
        description: 'Rolling element and plain bearings for mill equipment',
        parts: [
          {
            id: 'roll_neck_bearing',
            partNumber: 'RNB-4ROW-500',
            name: 'Four-Row Tapered Roll Neck Bearing',
            description: '500mm bore four-row tapered roller bearing for work roll necks',
            category: 'bearings',
            manufacturer: 'SKF',
            unitCost: 28000.00,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill'],
            failureModes: ['fatigue_spalling', 'cage_failure', 'lubrication_failure'],
            averageLifespan: { value: 18, unit: 'months' },
            specifications: { bore: '500mm', od: '720mm', type: 'Four-row tapered roller' },
          },
          {
            id: 'backup_roll_bearing',
            partNumber: 'BRB-CYL-650',
            name: 'Backup Roll Cylindrical Bearing',
            description: '650mm bore cylindrical roller bearing for backup rolls',
            category: 'bearings',
            manufacturer: 'Timken',
            unitCost: 45000.00,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill'],
            failureModes: ['surface_fatigue', 'misalignment_damage', 'contamination'],
            averageLifespan: { value: 24, unit: 'months' },
            specifications: { bore: '650mm', od: '920mm', type: 'Cylindrical roller' },
          },
          {
            id: 'crane_wheel_bearing',
            partNumber: 'CWB-SPHER-200',
            name: 'Crane Wheel Spherical Bearing',
            description: '200mm bore spherical roller bearing for crane wheels',
            category: 'bearings',
            manufacturer: 'FAG',
            unitCost: 3500.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'weeks' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['crane'],
            failureModes: ['fatigue_wear', 'contamination', 'inadequate_lubrication'],
            averageLifespan: { value: 36, unit: 'months' },
            specifications: { bore: '200mm', od: '360mm', type: 'Spherical roller' },
          },
        ],
      },
      {
        id: 'refractories',
        name: 'Refractories',
        description: 'Refractory materials for ladles, furnaces, and tundishes',
        parts: [
          {
            id: 'ladle_brick',
            partNumber: 'REF-MgC-LADLE',
            name: 'Magnesia-Carbon Ladle Brick',
            description: 'High-quality MgO-C brick for steel ladle lining',
            category: 'refractories',
            manufacturer: 'RHI Magnesita',
            unitCost: 85.00,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['ladle'],
            failureModes: ['erosion', 'spalling', 'slag_attack'],
            averageLifespan: { value: 80, unit: 'cycles' },
            specifications: { dimensions: '230x150x75mm', MgO: '95%', carbon: '12%' },
          },
          {
            id: 'tundish_board',
            partNumber: 'REF-INS-TUND',
            name: 'Tundish Insulation Board',
            description: 'Calcium silicate insulation board for tundish lining',
            category: 'refractories',
            manufacturer: 'Vesuvius',
            unitCost: 45.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'weeks' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['caster'],
            failureModes: ['thermal_degradation', 'mechanical_damage'],
            averageLifespan: { value: 1, unit: 'cycles' },
            specifications: { dimensions: '1000x500x25mm', density: '850kg/m3' },
          },
          {
            id: 'blast_furnace_brick',
            partNumber: 'REF-CARB-BF',
            name: 'Blast Furnace Carbon Brick',
            description: 'High-conductivity carbon brick for blast furnace hearth',
            category: 'refractories',
            manufacturer: 'Tokai Carbon',
            unitCost: 350.00,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['blast_furnace'],
            failureModes: ['erosion', 'brittleness', 'oxidation'],
            averageLifespan: { value: 15, unit: 'years' },
            specifications: { dimensions: '400x400x400mm', thermal_conductivity: '135W/mK' },
          },
        ],
      },
      {
        id: 'hydraulics',
        name: 'Hydraulic Components',
        description: 'Hydraulic cylinders, pumps, valves, and servo systems',
        parts: [
          {
            id: 'agc_cylinder',
            partNumber: 'HYD-AGC-350',
            name: 'AGC Hydraulic Cylinder',
            description: '350mm bore automatic gauge control cylinder for rolling mill',
            category: 'hydraulics',
            manufacturer: 'Rexroth',
            unitCost: 125000.00,
            currency: 'USD',
            leadTime: { value: 16, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill'],
            failureModes: ['seal_failure', 'rod_scoring', 'internal_leakage'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { bore: '350mm', stroke: '150mm', pressure: '315bar' },
          },
          {
            id: 'servo_valve',
            partNumber: 'HYD-SERVO-100',
            name: 'High-Response Servo Valve',
            description: '100 L/min servo valve for mill position control',
            category: 'hydraulics',
            manufacturer: 'Moog',
            unitCost: 8500.00,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill', 'caster'],
            failureModes: ['spool_wear', 'contamination', 'coil_failure'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { flow: '100 L/min', response: '10ms', filtration: 'NAS 6' },
          },
          {
            id: 'hydraulic_pump',
            partNumber: 'HYD-PUMP-250',
            name: 'Variable Displacement Piston Pump',
            description: '250cc/rev axial piston pump for mill hydraulic system',
            category: 'hydraulics',
            manufacturer: 'Parker',
            unitCost: 18500.00,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill', 'caster', 'crane'],
            failureModes: ['piston_wear', 'barrel_scoring', 'swash_plate_failure'],
            averageLifespan: { value: 25000, unit: 'hours' },
            specifications: { displacement: '250cc/rev', pressure: '350bar', speed: '1800rpm' },
          },
        ],
      },
      {
        id: 'electrical',
        name: 'Electrical Components',
        description: 'Motors, drives, transformers, and electrical equipment',
        parts: [
          {
            id: 'main_drive_motor',
            partNumber: 'MOT-DC-5000',
            name: 'Main Mill Drive Motor',
            description: '5000 kW DC motor for hot strip mill main drive',
            category: 'electrical',
            manufacturer: 'Siemens',
            unitCost: 850000.00,
            currency: 'USD',
            leadTime: { value: 40, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill'],
            failureModes: ['commutator_wear', 'insulation_breakdown', 'bearing_failure'],
            averageLifespan: { value: 25, unit: 'years' },
            specifications: { power: '5000kW', voltage: '750V DC', speed: '60/120rpm' },
          },
          {
            id: 'crane_hoist_motor',
            partNumber: 'MOT-AC-500',
            name: 'Crane Hoist Motor',
            description: '500 kW AC motor for overhead crane hoist',
            category: 'electrical',
            manufacturer: 'ABB',
            unitCost: 65000.00,
            currency: 'USD',
            leadTime: { value: 12, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['crane'],
            failureModes: ['winding_failure', 'bearing_wear', 'brake_failure'],
            averageLifespan: { value: 20, unit: 'years' },
            specifications: { power: '500kW', voltage: '6.6kV', duty: 'S4-60%' },
          },
          {
            id: 'mill_vfd',
            partNumber: 'VFD-MILL-2000',
            name: 'Mill Drive VFD',
            description: '2000 kW variable frequency drive for mill auxiliary drives',
            category: 'electrical',
            manufacturer: 'ABB',
            unitCost: 185000.00,
            currency: 'USD',
            leadTime: { value: 16, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill', 'annealing_furnace'],
            failureModes: ['igbt_failure', 'capacitor_degradation', 'control_board_failure'],
            averageLifespan: { value: 15, unit: 'years' },
            specifications: { power: '2000kW', voltage: '690V', cooling: 'Water-cooled' },
          },
        ],
      },
      {
        id: 'sensors',
        name: 'Sensors & Instrumentation',
        description: 'Temperature, level, thickness, and process measurement sensors',
        parts: [
          {
            id: 'pyrometer',
            partNumber: 'SENS-PYRO-1600',
            name: 'Ratio Pyrometer',
            description: 'Two-color ratio pyrometer for molten steel temperature measurement',
            category: 'sensors',
            manufacturer: 'Land Instruments',
            unitCost: 12500.00,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['blast_furnace', 'ladle', 'caster'],
            failureModes: ['lens_contamination', 'detector_degradation', 'calibration_drift'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { range: '700-1600C', accuracy: '0.5%', response: '10ms' },
          },
          {
            id: 'x_ray_gauge',
            partNumber: 'SENS-XRAY-THICK',
            name: 'X-Ray Thickness Gauge',
            description: 'Non-contact X-ray gauge for strip thickness measurement',
            category: 'sensors',
            manufacturer: 'Thermo Fisher',
            unitCost: 185000.00,
            currency: 'USD',
            leadTime: { value: 12, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['rolling_mill'],
            failureModes: ['x_ray_tube_failure', 'detector_failure', 'calibration_error'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { accuracy: '0.1%', speed: '2000m/min', radiation: '160kV' },
          },
          {
            id: 'mold_level_sensor',
            partNumber: 'SENS-EDDY-MOLD',
            name: 'Eddy Current Mold Level Sensor',
            description: 'Eddy current sensor for continuous caster mold level control',
            category: 'sensors',
            manufacturer: 'Berthold',
            unitCost: 25000.00,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['caster'],
            failureModes: ['coil_degradation', 'thermal_damage', 'slag_interference'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { range: '100mm', accuracy: '0.5mm', frequency: '100Hz' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Metals & Steel)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'wear',
        name: 'Wear & Erosion',
        description: 'Root causes related to material wear and erosion',
        icon: 'Eraser',
        color: '#f97316',
        rootCauses: [
          {
            id: 'roll_wear',
            name: 'Roll Surface Wear',
            description: 'Progressive deterioration of roll surface due to rolling contact and thermal cycling',
            category: 'wear',
            symptoms: [
              'Increasing roll force',
              'Strip surface quality degradation',
              'Roll profile deviation',
              'Chatter marks on product',
              'Increased vibration',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure roll profile with roll profiler',
                expectedResult: 'Profile deviation from target crown',
                tools: ['Roll profiler', 'Profile measurement system'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Inspect roll surface visually and with dye penetrant',
                expectedResult: 'Surface cracks, spalling, or roughness visible',
                tools: ['Dye penetrant kit', 'Surface roughness gauge'],
                duration: 45,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check roll surface hardness',
                expectedResult: 'Hardness degradation in wear zones',
                tools: ['Portable hardness tester'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Review tonnage rolled since last change',
                expectedResult: 'Compare to expected roll life',
                tools: ['Production records', 'Roll tracking system'],
                duration: 15,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Optimize roll cooling spray patterns',
              'Control rolling temperatures',
              'Use appropriate roll grades for product mix',
              'Implement roll rotation schedule',
              'Monitor roll surface condition regularly',
            ],
            typicalProbability: 0.25,
            affectedComponents: ['work_rolls', 'backup_rolls', 'roll_bearings'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 4, typical: 8, max: 16, unit: 'hours' },
            costRange: { min: 50000, max: 250000, currency: 'USD' },
            industryReferences: ['AIST Roll Technology Handbook'],
          },
          {
            id: 'refractory_erosion',
            name: 'Refractory Erosion',
            description: 'Chemical and mechanical erosion of refractory lining by molten steel and slag',
            category: 'wear',
            symptoms: [
              'Increased shell temperature',
              'Shortened vessel campaign life',
              'Steel quality issues',
              'Slag line wear pattern',
              'Hot spots on vessel exterior',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure remaining lining thickness with laser scanner',
                expectedResult: 'Thickness below minimum safe limit',
                tools: ['Laser lining scanner', 'Thermal camera'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Review shell temperature history',
                expectedResult: 'Temperature trending upward',
                tools: ['Process historian', 'IR thermometer'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Analyze slag chemistry',
                expectedResult: 'Aggressive slag composition identified',
                tools: ['Lab analysis'],
                duration: 120,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Optimize slag chemistry',
              'Control steel temperature',
              'Use appropriate refractory grade',
              'Implement regular lining measurement',
              'Monitor shell temperatures continuously',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['ladle_lining', 'tundish_lining', 'furnace_lining'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 8, typical: 24, max: 72, unit: 'hours' },
            costRange: { min: 20000, max: 150000, currency: 'USD' },
            industryReferences: ['AIST Steelmaking Refractories'],
          },
        ],
      },
      {
        id: 'thermal_stress',
        name: 'Thermal Stress',
        description: 'Root causes related to thermal cycling and temperature extremes',
        icon: 'Thermometer',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'thermal_fatigue',
            name: 'Thermal Fatigue Cracking',
            description: 'Crack initiation and growth due to repeated thermal cycling',
            category: 'thermal_stress',
            symptoms: [
              'Surface crack network (fire cracking)',
              'Spalling of material',
              'Heat checking patterns',
              'Reduced component life',
              'Increased maintenance frequency',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual inspection for crack patterns',
                expectedResult: 'Fire crack network or spalling visible',
                tools: ['Borescope', 'Magnifying glass', 'Dye penetrant'],
                duration: 45,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Review thermal cycling history',
                expectedResult: 'Excessive thermal cycles or rapid temperature changes',
                tools: ['Process historian'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Measure crack depth with ultrasonic testing',
                expectedResult: 'Crack depth assessment for safety',
                tools: ['UT flaw detector'],
                duration: 60,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Control heating and cooling rates',
              'Implement proper preheat procedures',
              'Use thermal fatigue resistant materials',
              'Minimize unnecessary thermal cycles',
              'Monitor temperature gradients',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['rolls', 'molds', 'refractories', 'burner_tips'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 4, typical: 16, max: 48, unit: 'hours' },
            costRange: { min: 10000, max: 100000, currency: 'USD' },
            industryReferences: ['ASM Failure Analysis Handbook'],
          },
          {
            id: 'overheating',
            name: 'Component Overheating',
            description: 'Damage from operation above design temperature limits',
            category: 'thermal_stress',
            symptoms: [
              'Discoloration of metal surfaces',
              'Softening or distortion',
              'Lubricant breakdown',
              'Seal failures',
              'Reduced mechanical properties',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check maximum recorded temperatures',
                expectedResult: 'Temperature exceeded design limit',
                tools: ['Process historian', 'Thermal camera'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Inspect cooling system',
                expectedResult: 'Blockages, flow restrictions, or pump issues',
                tools: ['Flow meter', 'Pressure gauge'],
                duration: 45,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check material hardness',
                expectedResult: 'Hardness reduction indicating over-tempering',
                tools: ['Hardness tester'],
                duration: 20,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Monitor operating temperatures continuously',
              'Maintain cooling systems properly',
              'Set temperature alarms and interlocks',
              'Train operators on temperature limits',
              'Implement predictive maintenance on cooling systems',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['bearings', 'rolls', 'hydraulic_systems', 'electrical_equipment'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' },
            costRange: { min: 5000, max: 75000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'mechanical',
        name: 'Mechanical Failure',
        description: 'Root causes related to mechanical components and forces',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'bearing_failure',
            name: 'Bearing Failure',
            description: 'Failure of rolling element or plain bearings due to fatigue, contamination, or improper lubrication',
            category: 'mechanical',
            symptoms: [
              'Elevated vibration at bearing frequencies',
              'Increased operating temperature',
              'Abnormal noise (grinding, rumbling)',
              'Metal particles in lubricant',
              'Shaft runout',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure vibration spectrum',
                expectedResult: 'Elevated peaks at BPFO, BPFI, BSF, or FTF frequencies',
                tools: ['Vibration analyzer'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Check bearing temperature',
                expectedResult: 'Temperature significantly above normal',
                tools: ['IR thermometer', 'Thermal camera'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Analyze lubricant sample',
                expectedResult: 'Metal particles, water, or degradation products',
                tools: ['Oil analysis lab'],
                duration: 120,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Inspect bearing upon removal',
                expectedResult: 'Identify failure mode (fatigue, contamination, etc.)',
                tools: ['Visual inspection', 'Magnification'],
                duration: 60,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement vibration-based monitoring',
              'Follow proper lubrication practices',
              'Control contamination',
              'Ensure proper mounting and alignment',
              'Use correct bearing for application',
            ],
            typicalProbability: 0.22,
            affectedComponents: ['roll_neck_bearings', 'motor_bearings', 'gearbox_bearings', 'crane_bearings'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 4, typical: 12, max: 36, unit: 'hours' },
            costRange: { min: 5000, max: 100000, currency: 'USD' },
            industryReferences: ['SKF Bearing Failure Analysis', 'ISO 10816'],
          },
          {
            id: 'gear_failure',
            name: 'Gear Tooth Failure',
            description: 'Damage to gear teeth from pitting, scoring, or tooth breakage',
            category: 'mechanical',
            symptoms: [
              'Abnormal gear mesh vibration',
              'Increased noise level',
              'Metal particles in gearbox oil',
              'Tooth profile deviation',
              'Gear backlash increase',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Analyze vibration at gear mesh frequency and sidebands',
                expectedResult: 'Elevated gear mesh frequency or sidebands',
                tools: ['Vibration analyzer'],
                duration: 30,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Inspect gearbox oil for metal particles',
                expectedResult: 'Ferrous particles indicating gear wear',
                tools: ['Oil analysis', 'Particle counter'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Borescope inspection of gear teeth',
                expectedResult: 'Visible pitting, scoring, or cracks',
                tools: ['Video borescope'],
                duration: 60,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Maintain proper lubrication',
              'Control gear alignment',
              'Avoid overloading',
              'Regular oil analysis',
              'Monitor vibration trends',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['mill_gearbox', 'crane_gearbox', 'pinion_stand'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 24, typical: 72, max: 168, unit: 'hours' },
            costRange: { min: 50000, max: 500000, currency: 'USD' },
            industryReferences: ['AGMA Gear Failure Modes'],
          },
        ],
      },
      {
        id: 'process',
        name: 'Process Related',
        description: 'Root causes related to process parameters and operating conditions',
        icon: 'Activity',
        color: '#10b981',
        rootCauses: [
          {
            id: 'breakout',
            name: 'Caster Breakout',
            description: 'Molten steel escaping through solidified shell in continuous caster',
            category: 'process',
            symptoms: [
              'Breakout prediction alarm',
              'Mold temperature anomaly',
              'Strand sticking',
              'Shell thickness variation',
              'Mold level instability',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review breakout prediction system data',
                expectedResult: 'Identify sticker or other breakout precursor',
                tools: ['Breakout detection system', 'Process historian'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Analyze mold thermocouple data',
                expectedResult: 'Temperature pattern indicating thin shell',
                tools: ['Process historian'],
                duration: 45,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Review casting parameters at time of incident',
                expectedResult: 'Parameter deviations from standards',
                tools: ['Process historian', 'Casting log'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Maintain breakout prediction system',
              'Control mold powder properties',
              'Optimize casting speed for grade',
              'Monitor mold heat flux',
              'Train operators on early warning signs',
            ],
            typicalProbability: 0.05,
            affectedComponents: ['mold', 'segments', 'spray_system', 'strand'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 4, typical: 12, max: 48, unit: 'hours' },
            costRange: { min: 100000, max: 2000000, currency: 'USD' },
            industryReferences: ['AIST Continuous Casting Guidelines'],
          },
          {
            id: 'cobble',
            name: 'Mill Cobble',
            description: 'Loss of control of strip in rolling mill causing material pile-up',
            category: 'process',
            symptoms: [
              'Strip tracking loss',
              'Tension deviation',
              'Speed mismatch between stands',
              'Looper position abnormality',
              'Strip break',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review process data leading up to cobble',
                expectedResult: 'Identify trigger (tension loss, tracking, speed)',
                tools: ['Process historian', 'Video replay'],
                duration: 45,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Inspect damaged equipment',
                expectedResult: 'Assess damage to guides, rolls, tables',
                tools: ['Visual inspection'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check incoming material properties',
                expectedResult: 'Material properties within specification',
                tools: ['Lab testing', 'Mill records'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Maintain guide and table equipment',
              'Control strip tracking with AGC and AFC',
              'Ensure proper threading speed',
              'Monitor material properties',
              'Train operators on cobble prevention',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['guides', 'tables', 'rolls', 'loopers'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 2, typical: 6, max: 24, unit: 'hours' },
            costRange: { min: 10000, max: 200000, currency: 'USD' },
            industryReferences: ['AIST Hot Strip Mill Guidelines'],
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'roll_degradation_cascade',
        name: 'Roll Degradation Cascade',
        description: 'Progressive roll deterioration leading to quality and productivity issues',
        sequence: [
          'Initial roll surface wear',
          'Roll profile deviation',
          'Strip thickness variation',
          'Increased roll force',
          'Accelerated wear',
          'Quality defects',
          'Unplanned roll change',
        ],
        indicators: [
          'Roll force trending upward',
          'Strip crown deviation',
          'Surface roughness increase',
        ],
        preventionStrategy: 'Implement roll profile monitoring and predictive roll change scheduling',
      },
      {
        id: 'refractory_failure_progression',
        name: 'Refractory Failure Progression',
        description: 'Gradual refractory deterioration leading to vessel failure',
        sequence: [
          'Initial slag line erosion',
          'Accelerated wear at hot spots',
          'Shell temperature increase',
          'Steel penetration risk',
          'Safety shutdown required',
        ],
        indicators: [
          'Shell temperature trending upward',
          'Lining thickness decreasing',
          'Increased slag aggressiveness',
        ],
        preventionStrategy: 'Continuous shell temperature monitoring and regular lining thickness measurement',
      },
    ],
  },

  // =============================================================================
  // LABOR RATES (Metals & Steel Industry)
  // =============================================================================
  laborRates: {
    roles: [
      { roleId: 'mill_technician', name: 'Mill Technician', hourlyRate: 55, currency: 'USD', certifications: ['Millwright', 'Hydraulics', 'High Temperature'] },
      { roleId: 'refractory_technician', name: 'Refractory Technician', hourlyRate: 52, currency: 'USD', certifications: ['Refractory Installation', 'Confined Space'] },
      { roleId: 'crane_operator', name: 'Crane Operator', hourlyRate: 48, currency: 'USD', certifications: ['CCO Certification', 'Hot Metal Handling'] },
      { roleId: 'electrician', name: 'Industrial Electrician', hourlyRate: 58, currency: 'USD', certifications: ['Journeyman License', 'Medium Voltage', 'Arc Flash'] },
      { roleId: 'instrumentation_technician', name: 'Instrumentation Technician', hourlyRate: 55, currency: 'USD', certifications: ['ISA CCST', 'Process Control'] },
      { roleId: 'metallurgist', name: 'Metallurgist', hourlyRate: 95, currency: 'USD', certifications: ['PE', 'ASM Certification'] },
      { roleId: 'reliability_engineer', name: 'Reliability Engineer', hourlyRate: 90, currency: 'USD', certifications: ['CRE', 'CMRP', 'Vibration Analyst'] },
      { roleId: 'process_engineer', name: 'Process Engineer', hourlyRate: 85, currency: 'USD', certifications: ['Six Sigma', 'Process Control'] },
      { roleId: 'process_analyst', name: 'Process Analyst', hourlyRate: 65, currency: 'USD', certifications: ['Data Analytics', 'Statistical Process Control'] },
      { roleId: 'contractor_mechanic', name: 'Contract Mechanic', hourlyRate: 70, currency: 'USD' },
      { roleId: 'contractor_electrician', name: 'Contract Electrician', hourlyRate: 80, currency: 'USD' },
      { roleId: 'contractor_refractory', name: 'Contract Refractory', hourlyRate: 65, currency: 'USD' },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
